<template>
  <div class="gap-20 md:flex">
    <div>
      <div v-if="hasHeadlineTasks" class="mb-20">
        <div class="heading-2 mb-12">
          {{ i18n(translations.headlineTasksTitle) }}
        </div>
        <div class="flex flex-wrap justify-center gap-16 md:justify-start">
          <OnboardingStep
            v-for="(step, index) in headlineTasks"
            :key="index"
            class="m-auto w-full min-w-[250px] max-w-[500px] md:m-0 md:w-auto md:max-w-[350px]"
            :step
          />
        </div>
      </div>
      <div class="mb-20">
        <div class="heading-2 mb-12">
          {{ i18n(translations.tasksToFinishTitle) }}
        </div>
        <OnboardingFeedback v-if="shouldDisplayFeedbackBlock" />
        <div
          v-if="hasTasksToFinish"
          class="flex flex-wrap justify-center gap-16 md:justify-start"
        >
          <OnboardingStep
            v-for="(step, index) in tasksToFinish"
            :key="index"
            class="m-auto w-full min-w-[250px] max-w-[500px] md:m-0 md:w-auto md:max-w-[350px]"
            :step
          />
        </div>
      </div>
      <div v-if="hasFinishedTasks" class="mb-20">
        <div class="heading-2 mb-12">
          {{ i18n(translations.finishedTasksTitle) }}
        </div>
        <div class="flex flex-wrap justify-center gap-16 md:justify-start">
          <OnboardingStep
            v-for="(step, index) in finishedTask"
            :key="index"
            class="m-auto w-full min-w-[250px] max-w-[500px] md:m-0 md:w-auto md:max-w-[350px]"
            :step
          />
        </div>
      </div>
    </div>
    <OnboardingSideBlock
      :is-fetching-pending="status === 'pending'"
      :on-going-required-tasks-amount
      :required-task-amount
    />
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage, useAsyncData } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import type { SelfOnboardingStep } from '~/api/seller-self-onboarding/types'

import { STEP_STATUS } from '../../Home.constants'

import translations from './OnboardingSteps.translations'
import OnboardingFeedback from './components/OnboardingFeedback/OnboardingFeedback.vue'
import OnboardingSideBlock from './components/OnboardingSideBlock/OnboardingSideBlock.vue'
import OnboardingStep from './components/OnboardingStep/OnboardingStep.vue'

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const $httpFetch = useHttpFetch()

const { data, error, status } = await useAsyncData(() =>
  $httpFetch('/bm/merchants/self-onboarding'),
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const {
  finishedTask,
  headlineTasks,
  tasksToFinish,
  requiredTaskAmount,
  onGoingRequiredTasksAmount,
} = (data.value?.selfOnboardingSteps || []).reduce(
  (tasksObject, task) => {
    let newTaskObject = tasksObject
    if (task.required) {
      newTaskObject = {
        ...newTaskObject,
        requiredTaskAmount: tasksObject.requiredTaskAmount + 1,
      }
    }
    if (task.status.code === STEP_STATUS.COMPLETED) {
      newTaskObject = {
        ...newTaskObject,
        finishedTask: [...tasksObject.finishedTask, task],
      }
    } else {
      if (task.displayAsHeadline) {
        newTaskObject = {
          ...newTaskObject,
          headlineTasks: [...tasksObject.headlineTasks, task],
        }
      } else {
        newTaskObject = {
          ...newTaskObject,
          tasksToFinish: [...tasksObject.tasksToFinish, task],
        }
      }
      if (task.required) {
        newTaskObject = {
          ...newTaskObject,
          onGoingRequiredTasksAmount:
            tasksObject.onGoingRequiredTasksAmount + 1,
        }
      }
    }

    return newTaskObject
  },
  {
    finishedTask: [],
    headlineTasks: [],
    tasksToFinish: [],
    requiredTaskAmount: 0,
    onGoingRequiredTasksAmount: 0,
  } as {
    finishedTask: SelfOnboardingStep[]
    headlineTasks: SelfOnboardingStep[]
    tasksToFinish: SelfOnboardingStep[]
    requiredTaskAmount: number
    onGoingRequiredTasksAmount: number
  },
)

const hasFinishedTasks = computed(() => !isEmpty(finishedTask))
const hasHeadlineTasks = computed(() => !isEmpty(headlineTasks))
const hasTasksToFinish = computed(() => !isEmpty(tasksToFinish))
const shouldDisplayFeedbackBlock = computed(
  () => onGoingRequiredTasksAmount === 0,
)
</script>
